import { SecondaryButton } from "@/components/buttons/SecondaryButton";
import { HistoryView } from "@/components/history/HistoryView";
import AntIcon from "@/components/icons/AntIcon";
import BackNavigationArrow from "@/components/views/BackNavigationArrow";
import { ItemCreatedUpdatedInfoView } from "@/components/views/ItemCreatedUpdatedInfoView";
import ContentWrapper from "@/components/wrappers/ContentWrapper";
import DisplayWrapper from "@/components/wrappers/DisplayWrapper";
import { appendSearchParamsToURL } from "@/utils/utils";
import { App as AntApp, Button, Card, Flex, Tabs, TabsProps, Tooltip } from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useGetAgent, useGetHistoryLogForAgent } from "../../../common/api/queries";
import { PageSizes } from "../../../common/constants";
import { Permission } from "../../../common/security/enums";
import { hasPermission } from "../../../common/security/utils";
import { EntityIdObject } from "../../../common/types";
import { useCurrentAgentAccount, useHasPermissions, useIsCurrentAgentTypePlus, usePermissions } from "../../auth/hooks";
import { CLIENT_ROUTE_PATHS } from "../../client/paths";
import { HistoryLogTable } from "../../historylog/components/HistoryLogTable";
import { AgentImportantInfo } from "../components/AgentImportantInfo";
import { AgentUserAccounts } from "../components/AgentUserAccounts";
import { AgentDetailView } from "../components/dataview/AgentDetailView";
import { SecondaryAgentDetailView } from "../components/dataview/SecondaryAgentDetailView";
import { AgentForm } from "../components/forms/agent/AgentForm";
import AgentBrandingForm from "../components/forms/AgentBrandingForm";
import { SecondaryAgentForm } from "../components/forms/secondaryagent/SecondaryAgentForm";
import { AgentAttachmentsTable } from "../components/tables/AgentAttachmentsTable";
import { AgentBrokerNumberTable } from "../components/tables/AgentBrokerNumberTable";
import { AgentCompetenceTable } from "../components/tables/AgentCompetenceTable";
import { AgentCompletedEducationTable } from "../components/tables/AgentCompletedEducationTable";
import { AgentLicenseTable } from "../components/tables/AgentLicenseTable";
import { AgentMentoringTable } from "../components/tables/AgentMentoringTable";
import { AgentUserInvitationsTable } from "../components/tables/AgentUserInvitationsTable";
import { AgentTypeTag } from "../components/tags/AgentTypeTag";
import { AGENT_ROUTE_PATHS } from "../paths";
import { Agent } from "../types";
import { formatAgentAggregatedName, formatAgentName, isLegalAgent } from "../utils";

const TAB = {
  AGENT: "agent",
  SECONDARY_AGENT: "secondary-agent",
  HISTORY: "history",
  IMPORTED_HISTORY: "imported-history",
  ATTACHMENTS: "attachments",
  COMPETENCES: "competences",
  LICENSES: "licenses",
  BROKER_NUMBERS: "broker-numbers",
  EDUCATION: "education",
  MENTORING: "mentoring",
  ACCOUNTS: "accounts",
  USER_INVITATIONS: "user-invitations",
  BRANDING: "branding",
};

export const AgentDetailContainer = () => {
  const { t } = useTranslation();
  const { modal } = AntApp.useApp();

  const navigate = useNavigate();

  const hasAgentCommissionsPermission = useHasPermissions(Permission.AGENT_COMMISSIONS);
  const hasProfileCommissionsPermission = useHasPermissions(Permission.PROFILE_COMMISSIONS);
  const userAccount = useCurrentAgentAccount();
  const permissions = usePermissions();
  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const { id } = useParams<EntityIdObject>() as { id: string };
  const [urlParams] = useSearchParams();

  const urlTabKey = urlParams.get("tab");

  const [agentUpdateMode, setAgentUpdateMode] = useState(false);
  const [historyLogPageIndex, setHistoryLogPageIndex] = useState<number>(0);

  const { data: agent, isError } = useGetAgent(id);
  const { data: historyLogData, isFetching } = useGetHistoryLogForAgent({
    id,
    object: { pageSize: PageSizes.LARGE, pageIndex: historyLogPageIndex },
  });

  const {
    hasAgentUpdatePermission,
    hasAgentCompetencePermission,
    hasAgentCompetenceManagePermission,
    hasAgentLicensePermission,
    hasAgentLicenseManagePermission,
    hasAgentBrokerNumberPermission,
    hasAgentBrokerNumberManagePermission,
    hasAdminUsersPermission,
    hasAgentUserInvitePermission,
    hasAdminTopAgentsPermission,
    hasAgentEducationPermission,
    hasAgentEducationManagePermission,
    hasAgentMentoringPermission,
    hasAgentMentoringManagePermission,
  } = useMemo(
    () => ({
      hasAgentUpdatePermission: hasPermission(permissions, Permission.AGENT_UPDATE),
      hasAgentCompetencePermission: hasPermission(permissions, Permission.AGENT_COMPETENCE),
      hasAgentCompetenceManagePermission: hasPermission(permissions, Permission.AGENT_COMPETENCE_MANAGE),
      hasAgentLicensePermission: hasPermission(permissions, Permission.AGENT_LICENSE),
      hasAgentLicenseManagePermission: hasPermission(permissions, Permission.AGENT_LICENSE_MANAGE),
      hasAgentBrokerNumberPermission: hasPermission(permissions, Permission.AGENT_BROKER_NUMBER),
      hasAgentBrokerNumberManagePermission: hasPermission(permissions, Permission.AGENT_BROKER_NUMBER_MANAGE),
      hasAdminUsersPermission: hasPermission(permissions, Permission.ADMIN_USERS),
      hasAgentUserInvitePermission: hasPermission(permissions, Permission.AGENT_USER_INVITE),
      hasAdminTopAgentsPermission: hasPermission(permissions, Permission.ADMIN_TOP_AGENTS),
      hasAgentEducationPermission: hasPermission(permissions, Permission.AGENT_EDUCATION),
      hasAgentEducationManagePermission: hasPermission(permissions, Permission.AGENT_EDUCATION_MANAGE),
      hasAgentMentoringPermission: hasPermission(permissions, Permission.AGENT_MENTORING),
      hasAgentMentoringManagePermission: hasPermission(permissions, Permission.AGENT_MENTORING_MANAGE),
    }),
    [permissions],
  );

  const resolveTabKey = () => {
    switch (urlTabKey) {
      case TAB.AGENT:
      case TAB.HISTORY:
      case TAB.IMPORTED_HISTORY:
      case TAB.ATTACHMENTS:
        return urlTabKey;
      case TAB.SECONDARY_AGENT:
        return isTopAgentPlus ? urlTabKey : TAB.AGENT;
      case TAB.COMPETENCES:
        return hasAgentCompetencePermission ? urlTabKey : TAB.AGENT;
      case TAB.LICENSES:
        return hasAgentLicensePermission ? urlTabKey : TAB.AGENT;
      case TAB.BROKER_NUMBERS:
        return hasAgentBrokerNumberPermission ? urlTabKey : TAB.AGENT;
      case TAB.EDUCATION:
        return hasAgentEducationPermission && isTopAgentPlus ? urlTabKey : TAB.AGENT;
      case TAB.MENTORING:
        return hasAgentMentoringPermission ? urlTabKey : TAB.AGENT;
      case TAB.ACCOUNTS:
        return hasAdminUsersPermission ? urlTabKey : TAB.AGENT;
      case TAB.USER_INVITATIONS:
        return hasAgentUserInvitePermission && !hasAdminUsersPermission ? urlTabKey : TAB.AGENT;
      case TAB.BRANDING:
        return hasAdminTopAgentsPermission ? urlTabKey : TAB.AGENT;
      default:
        return TAB.AGENT;
    }
  };

  const tabKey = resolveTabKey();

  const moveViewToTab = (tabKey: string) => {
    navigate(appendSearchParamsToURL({ tab: tabKey, pageIndex: undefined }), { replace: true });
  };

  const handleTabKeyChange = (key: string) => {
    if ((tabKey === TAB.AGENT || tabKey === TAB.SECONDARY_AGENT) && agentUpdateMode) {
      modal.confirm({
        title: t("agent.helpers.unsavedChanges"),
        okText: t("agent.helpers.unsavedChangesConfirm"),
        cancelText: t("common.back"),
        onOk: () => {
          moveViewToTab(key);
          setAgentUpdateMode(false);
        },
      });
    } else {
      moveViewToTab(key);
    }
  };

  const getTabsItems = (agent: Agent) => {
    const items: TabsProps["items"] = [
      {
        key: TAB.AGENT,
        icon: <AntIcon type="solution" />,
        label: t("agent.titles.data"),
        children: agentUpdateMode ? (
          <AgentForm agent={agent} onCancelClick={() => setAgentUpdateMode(false)} />
        ) : (
          <>
            <ItemCreatedUpdatedInfoView item={agent} className="margin-top-tiny" />
            <AgentDetailView agent={agent} onUpdateClick={() => setAgentUpdateMode(true)} />
          </>
        ),
      },
      isTopAgentPlus
        ? {
            key: TAB.SECONDARY_AGENT,
            icon: <AntIcon type="solution" />,
            label: t("agent.titles.secondaryAgentData"),
            children: agentUpdateMode ? (
              <SecondaryAgentForm agentId={id} onCancel={() => setAgentUpdateMode(false)} />
            ) : (
              <SecondaryAgentDetailView
                agentId={id}
                agentSecondaryStatus={agent.secondaryStatus}
                onUpdate={() => setAgentUpdateMode(true)}
              />
            ),
          }
        : undefined,
      {
        key: TAB.HISTORY,
        icon: <AntIcon type="history" />,
        label: t("agent.titles.history"),
        children: <HistoryView item={agent} translationRootPath="agent.attrs" type="agent" />,
      },
    ].filter((item) => !!item);

    if (historyLogData?.pageData.length) {
      items.push({
        key: TAB.IMPORTED_HISTORY,
        icon: <AntIcon type="import" />,
        label: t("agent.titles.importedHistory"),
        children: <HistoryLogTable onChangePage={setHistoryLogPageIndex} loading={isFetching} data={historyLogData} />,
      });
    }

    items.push({
      key: TAB.ATTACHMENTS,
      icon: <AntIcon type="paper-clip" />,
      label: t("agent.titles.attachments"),
      children: (
        <AgentAttachmentsTable
          agent={agent}
          hasUpload={hasAgentUpdatePermission}
          hasDelete={hasAgentUpdatePermission}
        />
      ),
    });

    if (hasAgentCompetencePermission) {
      items.push({
        key: TAB.COMPETENCES,
        icon: <AntIcon type="file-done" />,
        label: t("agent.titles.competences"),
        children: (
          <AgentCompetenceTable agentId={agent.id} showAllColumns showActions={hasAgentCompetenceManagePermission} />
        ),
      });
    }

    if (hasAgentLicensePermission) {
      items.push({
        key: TAB.LICENSES,
        icon: <AntIcon type="bank" />,
        label: t("agent.titles.licenses"),
        children: (
          <AgentLicenseTable
            agentId={agent.id}
            defaultLicenseValue={agent.nbsRegistrationNumber}
            showAllColumns
            showActions={hasAgentLicenseManagePermission}
          />
        ),
      });
    }

    if (hasAgentBrokerNumberPermission) {
      items.push({
        key: TAB.BROKER_NUMBERS,
        icon: <AntIcon type="id-card" />,
        label: t("agent.titles.brokerNumbers"),
        children: (
          <AgentBrokerNumberTable
            agentId={agent.id}
            showAllColumns
            showActions={hasAgentBrokerNumberManagePermission}
          />
        ),
      });
    }

    if (hasAgentEducationPermission && isTopAgentPlus) {
      items.push({
        key: TAB.EDUCATION,
        icon: <AntIcon type="reconciliation" />,
        label: t("agent.titles.education"),
        children: <AgentCompletedEducationTable agentId={agent.id} showActions={hasAgentEducationManagePermission} />,
      });
    }

    if (hasAgentMentoringPermission) {
      items.push({
        key: TAB.MENTORING,
        icon: <AntIcon type="projection" />,
        label: t("agent.titles.mentoring"),
        children: <AgentMentoringTable agentId={agent.id} showActions={hasAgentMentoringManagePermission} />,
      });
    }

    if (hasAdminUsersPermission) {
      items.push({
        key: TAB.ACCOUNTS,
        icon: <AntIcon type="user-switch" />,
        label: t("agent.titles.users"),
        children: <AgentUserAccounts agent={agent} />,
      });
    }

    if (hasAgentUserInvitePermission && !hasAdminUsersPermission) {
      items.push({
        key: TAB.USER_INVITATIONS,
        icon: <AntIcon type="mail" />,
        label: t("agent.titles.userInvitations"),
        children: <AgentUserInvitationsTable agentId={agent.id} />,
      });
    }

    if (hasAdminTopAgentsPermission) {
      items.push({
        key: TAB.BRANDING,
        icon: <AntIcon type="bg-colors" />,
        label: t("agent.titles.branding"),
        children: <AgentBrandingForm agent={agent} />,
      });
    }

    return items;
  };

  return (
    <DisplayWrapper itemLoaded={!!agent} isError={isError}>
      {agent && (
        <ContentWrapper>
          <Card
            className="card-box"
            title={
              <div>
                <BackNavigationArrow>
                  <Flex align="center">
                    <AgentTypeTag type={agent.type} />

                    <h2>{formatAgentAggregatedName(agent)}</h2>

                    {agent.partnerClientId && (
                      <Tooltip title={t("agent.actions.showClientDetail")}>
                        <Button
                          color="primary"
                          variant="outlined"
                          className="margin-left-small"
                          style={{ flexShrink: 0 }}
                          icon={<AntIcon type="user-switch" />}
                          href={generatePath(CLIENT_ROUTE_PATHS.detail.to, { id: agent.partnerClientId })}
                          target="_blank"
                        />
                      </Tooltip>
                    )}

                    {isLegalAgent(agent) && agent.representatives.length ? (
                      <span className="sub-header-info standard-text margin-left-small">
                        <span>{t("agent.attrs.representativeIds")}:</span>{" "}
                        {agent.representatives?.map((representative) => formatAgentName(representative)).join(", ")}
                      </span>
                    ) : undefined}
                  </Flex>
                </BackNavigationArrow>
              </div>
            }
            extra={
              hasAgentCommissionsPermission &&
              (hasProfileCommissionsPermission || agent.id !== userAccount?.agent.id) && (
                <Link to={generatePath(AGENT_ROUTE_PATHS.commissions.to, { id: agent.id })}>
                  <SecondaryButton icon={<AntIcon type="euro" />}>
                    {t("agent.actions.goToAgentCommissions")}
                  </SecondaryButton>
                </Link>
              )
            }
          >
            <AgentImportantInfo agent={agent} />

            <Tabs activeKey={tabKey} onChange={handleTabKeyChange} items={getTabsItems(agent)} />
          </Card>
        </ContentWrapper>
      )}
    </DisplayWrapper>
  );
};
